import { Controller, useForm } from 'react-hook-form';

import { useOnboardingContext } from '@/contexts/onboardingContext';
import useTranslate from '@/hooks/useTranslate';
import { STEPS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import { PhoneNumberFormValues } from '@/pages/members/pages/Onboarding/steps/PhoneNumberStep/types';
import StepComponent from '@/pages/members/pages/Onboarding/steps/StepComponent';
import { StepProps } from '@/pages/members/pages/Onboarding/steps/types';
import getSubmittedData from '@/pages/members/pages/Onboarding/utils/getSubmittedData';
import Alert from '@/ui/members/components/Alert';
import FieldError from '@/ui/members/components/form/FieldError';
import InputGroup from '@/ui/members/components/form/InputGroup';
import PhoneInput from '@/ui/members/components/form/PhoneInput';
import Heading from '@/ui/members/components/typography/Heading/Heading';
import UL from '@/ui/members/components/typography/UL';

interface Props extends StepProps {
  /**
   * Whether to show all content or a minimal version
   */
  showAllContent?: boolean;
  subheaderTextKey?: string;
  bulletPointTextKey?: string | null;
}

const PhoneNumberStep = ({
  nodeType,
  showAllContent = true,
  subheaderTextKey = 'subheading',
  bulletPointTextKey = 'smsDisclosure',
  uid
}: Props) => {
  const onboardingContext = useOnboardingContext();
  const { t } = useTranslate(`${STEPS_PATH_TRANSLATION_KEY}.PhoneNumberStep`);
  const submittedData = getSubmittedData<PhoneNumberFormValues>(
    nodeType,
    uid,
    onboardingContext.steps
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<PhoneNumberFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      phoneNumber: submittedData?.phoneNumber || ''
    }
  });

  return (
    <StepComponent uid={uid} nodeType={nodeType} handleSubmit={handleSubmit} isValid={isValid}>
      <div className="text-center mb-6">
        <Heading className="mb-3" value={1}>
          {t('heading')}
        </Heading>

        {showAllContent && <p className="text-base mb-3">{t(subheaderTextKey)}</p>}
      </div>

      {onboardingContext.stepError && (
        <Alert
          level="error"
          message={`This phone number couldn't be saved. ${onboardingContext.stepError}`}
          dismissable={false}
        />
      )}

      <InputGroup>
        <Controller
          name="phoneNumber"
          control={control}
          rules={{
            required: t('error.required'),
            pattern: {
              value: /^(?:\+1)?\s?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
              message: t('error.invalidFormat')
            }
          }}
          render={({ field }) => (
            <PhoneInput
              autoFocus
              label={t('phoneInputLabel')}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />

        <FieldError error={errors.phoneNumber} />
      </InputGroup>

      {bulletPointTextKey && (
        <div className="my-3">
          <UL listItems={[t(bulletPointTextKey)]} />
        </div>
      )}
    </StepComponent>
  );
};

export default PhoneNumberStep;
